export class ApiError extends Error {
  status: number;
  body?: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface ValidationErrorResponse {
  result?: Record<string, string[]>;
  success: boolean;
  message?: string;
}

const GENERIC_ERROR_MESSAGE =
  "We're having trouble processing your request right now. Please check your information and try again. If the problem continues, please contact our support team for assistance.";

/**
 * Parses API validation error responses and returns a user-friendly error message.
 * Handles the standard validation error format: { result: { fieldName: ["error message"] }, success: false, message: "..." }
 *
 * @param error - The error object caught from an API call
 * @returns A user-friendly error message string
 */
export function parseValidationError(error: unknown): string {
  try {
    // Handle custom ApiError format
    if (error instanceof ApiError && error.body) {
      const errorData = error.body as ValidationErrorResponse;

      if (errorData?.result && typeof errorData.result === 'object') {
        const messages: string[] = [];
        for (const [, fieldErrors] of Object.entries(errorData.result)) {
          if (Array.isArray(fieldErrors)) {
            messages.push(...fieldErrors);
          }
        }
        if (messages.length > 0) {
          return messages.join(' ');
        }
      }

      //only show message from the API response for 400 Bad Request errors, as other error statuses
      // may indicate server issues rather than user input errors
      if (error.status === 400 && errorData?.message) {
        return errorData.message;
      }
    }

    // Handle axios-style error responses (for backward compatibility)
    const errorData = (error as { response?: { data?: ValidationErrorResponse } })?.response?.data;

    if (errorData?.result && typeof errorData.result === 'object') {
      // Extract field-specific validation messages
      const messages: string[] = [];
      for (const [, fieldErrors] of Object.entries(errorData.result)) {
        if (Array.isArray(fieldErrors)) {
          messages.push(...fieldErrors);
        }
      }
      if (messages.length > 0) {
        return messages.join(' ');
      }
    }

    // Fall back to the general message if available
    if (errorData?.message) {
      return errorData.message;
    }
  } catch {
    // Parsing failed, fall through to generic message
  }

  // Generic user-friendly error message
  return GENERIC_ERROR_MESSAGE;
}
